import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Heart, Utensils, FileText, ArrowRight, Sun, Moon, Sunrise } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const [todayAffirmation, setTodayAffirmation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodayAffirmation();
  }, []);

  const fetchTodayAffirmation = async () => {
    try {
      const response = await axios.get(`${API}/affirmations/today`);
      setTodayAffirmation(response.data);
    } catch (error) {
      console.error("Error fetching affirmation:", error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good Morning", icon: Sunrise };
    if (hour < 18) return { text: "Good Afternoon", icon: Sun };
    return { text: "Good Evening", icon: Moon };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  const quickLinks = [
    {
      title: "Affirmation Calendar",
      description: "Daily healing affirmations",
      icon: Calendar,
      color: "#4FB3D4",
      bgColor: "bg-[#4FB3D4]/10",
      link: "/calendar"
    },
    {
      title: "Health Recipes",
      description: "Nourishing meals for energy",
      icon: Utensils,
      color: "#FFB7B2",
      bgColor: "bg-[#FFB7B2]/20",
      link: "/recipes"
    },
    {
      title: "Bonus PDF Guide",
      description: "15 Ways to Stop a Narcissist",
      icon: FileText,
      color: "#A69BE0",
      bgColor: "bg-[#A69BE0]/20",
      link: "/bonus"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      
      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="dashboard-section">
        <div className="container mx-auto max-w-6xl">
          {/* Welcome Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#4FB3D4]/10 flex items-center justify-center">
                <GreetingIcon className="h-6 w-6 text-[#4FB3D4]" />
              </div>
              <div>
                <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0]">
                  {greeting.text}
                </p>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A]">
                  Welcome to Your Recovery Space
                </h1>
              </div>
            </div>
            <p className="text-lg text-[#1A2A3A]/60 max-w-2xl">
              Every day is a step forward on your healing journey. Take it one moment at a time.
            </p>
          </div>

          {/* Today's Affirmation */}
          <Card className="affirmation-card mb-12 shadow-soft" data-testid="todays-affirmation">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 font-heading text-xl text-[#1A2A3A]">
                <Heart className="h-6 w-6 text-[#FFB7B2]" />
                Today's Affirmation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-[#4FB3D4]/10 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-[#4FB3D4]/10 rounded w-1/4"></div>
                </div>
              ) : todayAffirmation ? (
                <>
                  <p className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-4 leading-relaxed">
                    "{todayAffirmation.text}"
                  </p>
                  <span className="inline-block px-4 py-2 rounded-full bg-[#4FB3D4]/10 text-[#4FB3D4] text-sm font-medium capitalize">
                    {todayAffirmation.category}
                  </span>
                </>
              ) : (
                <p className="font-heading text-2xl font-semibold text-[#1A2A3A]">
                  "You are worthy of love and healing."
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Links Grid */}
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6">
              Your Recovery Toolkit
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {quickLinks.map((item, index) => (
                <Link to={item.link} key={index}>
                  <Card 
                    className="bg-white rounded-2xl shadow-card border border-[#E5E7EB]/50 p-6 hover:shadow-floating transition-all duration-300 card-hover h-full"
                    data-testid={`quick-link-${index}`}
                  >
                    <CardContent className="p-0">
                      <div className={`w-14 h-14 rounded-2xl ${item.bgColor} flex items-center justify-center mb-4`}>
                        <item.icon className="h-7 w-7" style={{ color: item.color }} />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#1A2A3A]/60 mb-4">
                        {item.description}
                      </p>
                      <span className="inline-flex items-center text-[#4FB3D4] font-medium">
                        Explore
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Progress Section */}
          <Card className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-2xl border-0 p-8" data-testid="progress-section">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-2">
                  Remember: Healing Is Not Linear
                </h3>
                <p className="text-[#1A2A3A]/60">
                  Some days will be harder than others. That's okay. You're still making progress.
                </p>
              </div>
              <Link to="/calendar">
                <Button 
                  className="btn-primary rounded-full px-6 py-4 font-semibold"
                  data-testid="view-calendar-btn"
                >
                  View Calendar
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="text-white/70 hover:text-white transition-colors">About</Link>
              <Link to="/bonus" className="text-white/70 hover:text-white transition-colors">Resources</Link>
            </nav>
            <p className="text-white/50 text-sm">© 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DashboardPage;
